import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { ColorPicker } from '@atlassian/color-picker/ui';
import { isReadable } from '@atlassian/color-picker/utils/color-conversion';
import { useAutoTransitioningColors } from '@atlassian/color-picker/utils/use-autotransitioning-colors';
import { useBuiltInColorMap } from '@atlassian/color-picker/utils/use-built-in-color-map';
import { ImageField } from '@atlassian/company-hub-common/config-panel/image-field';
import {
	UnsplashSearchField,
	type UnsplashSearchFieldProps,
} from '@atlassian/company-hub-common/config-panel/unsplash-search-field';
import { useProductDependencies } from '@atlassian/company-hub-common/controllers/use-product-dependencies';
import { useHubAutoTextColor } from '@atlassian/company-hub-common/hub-auto-text-color';
import {
	ELEMENT_DEFAULTS,
	resolveAdfColorForPicker,
} from '@atlassian/company-hub-common/hub-brand-palette';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';
import { ManageTranslationsButton } from '@atlassian/company-hub-common/manage-translations';
import { CommonLayout } from '@atlassian/company-hub-common/types';
import { useIsTranslationSupportEnabled } from '@atlassian/company-hub-common/use-is-translation-support-enabled';

import { carouselConfigAnalyticSource, carouselExtensionType } from '../../../../../constants';
import { frameNewParametersV2 } from '../../../../../controllers/use-carousel-extension-parameters-and-actions';
import { type UpdateFrameFormFieldType } from '../../../../../controllers/use-carousel-extension-parameters-and-actions/types';
import { FRAME_FIELDS } from '../../../../../types';
import { CarouselLayoutOptions } from '../../../fields/layout-options';
import { FrameField, type FrameFieldProps } from './frame-field';
import { messages } from './messages';

export type FrameContentProps = {
	showBgAndTextColorOptions?: boolean;
	showLayout?: boolean;
	showMediaPicker?: boolean;
	updateFrameFormField: UpdateFrameFormFieldType;
} & Pick<FrameFieldProps, 'frameParameters' | 'isDisabled'> &
	Pick<UnsplashSearchFieldProps, 'contentId' | 'initialUnsplashResults'>;

const styles = cssMap({
	wrapper: {
		alignSelf: 'stretch',
		flexDirection: 'column',
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	bgAndTextColorOptionsWrapper: {
		marginTop: token('space.200'),
	},
	manageTranslationsButtonWrapper: {
		alignSelf: 'stretch',
		flexDirection: 'column',
		marginTop: token('space.200'),
	},
});

export const FrameContent = ({
	contentId,
	frameParameters,
	initialUnsplashResults,
	isDisabled,
	showBgAndTextColorOptions,
	updateFrameFormField,
	showMediaPicker,
	showLayout,
}: FrameContentProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		cardId,
		imageSrc,
		imageAltText,
		confluencePageId,
		backgroundColor = frameNewParametersV2.backgroundColor,
		textColor = frameNewParametersV2.textColor,
		layout,
	} = frameParameters;
	const { isCompanyHub, enableLinkInputInMediaPicker, isSSRProductInDarkMode, branding } =
		useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};
	const { backgroundColorMap, textColorMap, emptyColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);

	const theming = useHubTheming();
	const isV2 = !!theming?.isAdvancedThemingEnabled;
	const palette = isV2 ? theming.palette : undefined;
	const primaryColor = isV2 ? theming.primaryColor : undefined;
	const v2BrandMap = isV2 ? theming.brandBackgroundColorMap : undefined;

	// Resolve to literal hex so isReadable in useAutoTransitioningColors works
	// against the actual colour values, not against CSS var refs (which would
	// silently fail).
	const resolvedBackgroundColor =
		isV2 && palette && primaryColor
			? resolveAdfColorForPicker(backgroundColor, palette, 'primary.200', primaryColor)
			: backgroundColor;
	// Show the colour the canvas actually paints: each frame re-derives an
	// auto-managed black/white against its background at render time. Resolved
	// from the raw frame values, exactly as `carousel-content` does.
	const resolveTextColor = useHubAutoTextColor('carousel-extension');
	const displayedTextColor = fg('hubs_config_panel_text_color_parity')
		? (resolveTextColor({
				backgroundColor: frameParameters.backgroundColor,
				textColor: frameParameters.textColor,
				defaultRef: ELEMENT_DEFAULTS['carousel.bg'],
			}) ?? textColor)
		: textColor;
	const resolveTextForPicker = (color: string) =>
		isV2 && palette && primaryColor
			? resolveAdfColorForPicker(
					color,
					palette,
					'primary.1000',
					isReadable('#FFFFFF', primaryColor) ? '#FFFFFF' : '#000000',
					primaryColor,
				)
			: color;
	const resolvedTextColor = resolveTextForPicker(displayedTextColor);

	const isReadableBackgroundColor = (color: string) => isReadable(color, resolvedTextColor);
	const isReadableTextColor = (color: string) => isReadable(color, resolvedBackgroundColor);
	const isTranslationEnabled = useIsTranslationSupportEnabled();

	const toStoredColor = isV2 ? theming.toStoredColor : undefined;

	const updateBackgroundColor = (bgColor: string) => {
		const next = toStoredColor ? toStoredColor(bgColor) : bgColor;
		updateFrameFormField(cardId, {
			[FRAME_FIELDS.BACKGROUND_COLOR]: next,
		});
	};
	const updateTextColor = (color: string) => {
		const next = toStoredColor ? toStoredColor(color) : color;
		updateFrameFormField(cardId, {
			[FRAME_FIELDS.TEXT_COLOR]: next,
		});
	};

	const canShowImagePicker = layout !== CommonLayout.NO_IMAGE_BACKGROUND;

	useAutoTransitioningColors(
		resolvedBackgroundColor,
		isReadableTextColor,
		// The stored value, not the derived one: this hook writes to the ADF when
		// its input is unreadable, and the derived colour is already legible.
		resolveTextForPicker(textColor),
		updateTextColor,
		branding?.defaultParameters,
	);

	return (
		<Flex xcss={styles.wrapper}>
			{showLayout && (
				<CarouselLayoutOptions
					frameParameters={frameParameters}
					updateFrameFormField={updateFrameFormField}
					contentId={contentId}
				/>
			)}
			<FrameField
				frameParameters={frameParameters}
				isDisabled={isDisabled}
				updateFrameFormField={updateFrameFormField}
			/>
			{isTranslationEnabled && (
				<Flex xcss={styles.manageTranslationsButtonWrapper}>
					<ManageTranslationsButton extensionType={carouselExtensionType} itemId={cardId} />
				</Flex>
			)}
			{canShowImagePicker &&
				(showMediaPicker ? (
					<ImageField
						shouldAllowLinkFromUrl={enableLinkInputInMediaPicker}
						analyticsSource={carouselConfigAnalyticSource}
						itemId={cardId}
						contentId={contentId}
						imageSrc={imageSrc}
						imageAltText={imageAltText}
						updateItemFormField={updateFrameFormField}
					/>
				) : (
					<UnsplashSearchField
						analyticsSource={carouselConfigAnalyticSource}
						idPrefix="frame"
						itemId={cardId}
						contentId={contentId}
						confluencePageId={confluencePageId}
						imageSrc={imageSrc}
						imageAltText={imageAltText}
						initialUnsplashResults={initialUnsplashResults}
						isNoImageAllowed={false}
						updateItemFormField={updateFrameFormField}
					/>
				))}
			{showBgAndTextColorOptions && (
				<Stack space="space.200" xcss={styles.bgAndTextColorOptionsWrapper}>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text">
							<FormattedMessage {...messages.backgroundColor} />
						</Text>
						<ColorPicker
							builtInColorMap={
								v2BrandMap
									? v2BrandMap
									: hideDefaultColorPalette && brandBackgroundColorMap
										? brandBackgroundColorMap
										: backgroundColorMap
							}
							isReadableColor={isReadableBackgroundColor}
							onChange={updateBackgroundColor}
							value={resolvedBackgroundColor}
							pickerTitle={messages.backgroundColor}
							validationSuccessMessage={formatMessage(messages.colorValidationSuccessMessage)}
							validationFailMessage={formatMessage(messages.colorValidationFailMessage)}
							showCustomPalette={isCompanyHub}
							analyticsSource={carouselConfigAnalyticSource}
						/>
					</Inline>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text">
							<FormattedMessage {...messages.textColor} />
						</Text>
						<ColorPicker
							builtInColorMap={
								v2BrandMap ? v2BrandMap : hideDefaultColorPalette ? emptyColorMap : textColorMap
							}
							isReadableColor={isReadableTextColor}
							onChange={updateTextColor}
							value={resolvedTextColor}
							pickerTitle={messages.textColor}
							validationSuccessMessage={formatMessage(messages.colorValidationSuccessMessage)}
							validationFailMessage={formatMessage(messages.colorValidationFailMessage)}
							showCustomPalette={isCompanyHub}
							analyticsSource={carouselConfigAnalyticSource}
							showTextIcon
						/>
					</Inline>
				</Stack>
			)}
		</Flex>
	);
};
